import { logger } from './logger';

// Track active fetch operations to allow cancellation
let activeAbortController: AbortController | null = null;

/**
 * Detect if running on a mobile device
 * Used to skip expensive waveform generation on mobile
 */
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check for mobile user agent - includes tablets which can also crash
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(navigator.userAgent);

  // Check for touch device (phones, tablets)
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Skip waveform on ANY mobile/tablet device - they can all run out of memory
  // Previous logic was too strict (required small screen + touch + mobile UA)
  return isMobileUA || (isTouchDevice && window.innerWidth < 1024);
};

/**
 * Generate waveform data from an audio file
 * Returns an array of amplitudes normalized between 0 and 1
 *
 * NOTE: On mobile devices, returns a placeholder waveform to avoid
 * memory crashes from decoding large audio files in the browser.
 */
export const generateWaveformData = async (
  audioUrl: string,
  samples: number = 100
): Promise<number[]> => {
  // Skip expensive waveform generation on mobile to prevent crashes
  if (isMobileDevice()) {
    logger.info('[Waveform] Skipping generation on mobile device');
    // Return a gentle sine wave pattern as placeholder
    return Array(samples).fill(0).map((_, i) =>
      0.3 + 0.2 * Math.sin((i / samples) * Math.PI * 4)
    );
  }
  // Cancel any previous waveform generation in progress
  if (activeAbortController) {
    activeAbortController.abort();
  }
  activeAbortController = new AbortController();
  const signal = activeAbortController.signal;

  let audioContext: AudioContext | null = null;

  try {
    // Fetch the audio file with abort signal
    const response = await fetch(audioUrl, { signal });
    if (signal.aborted) {
      return Array(samples).fill(0.5);
    }
    const arrayBuffer = await response.arrayBuffer();

    // Create audio context
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Get the raw audio data from the first channel
    const rawData = audioBuffer.getChannelData(0);
    const blockSize = Math.floor(rawData.length / samples);
    const waveformData: number[] = [];

    // Process audio data into samples
    for (let i = 0; i < samples; i++) {
      const start = i * blockSize;
      let sum = 0;

      // Get RMS (Root Mean Square) for each block
      for (let j = 0; j < blockSize; j++) {
        sum += rawData[start + j] ** 2;
      }

      const rms = Math.sqrt(sum / blockSize);
      waveformData.push(rms);
    }

    // Normalize the waveform data
    const max = Math.max(...waveformData);
    const normalizedData = max > 0 ? waveformData.map(val => val / max) : waveformData;

    return normalizedData;
  } catch (error) {
    // Don't log abort errors as they're intentional
    if (error instanceof Error && error.name === 'AbortError') {
      return Array(samples).fill(0.5);
    }
    logger.error('Error generating waveform', error);
    // Return a default flat waveform on error
    return Array(samples).fill(0.5);
  } finally {
    // Always close the AudioContext to prevent memory leaks
    if (audioContext) {
      try {
        await audioContext.close();
      } catch {
        // Ignore close errors
      }
    }
  }
};
